import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { staffApi, ApiError } from "@/api";
import type { BlockDocumentJson, LineItem, Quote } from "@/types";
import { type QuoteContext } from "@/renderer";
import { Button, FullPageSpinner } from "@/components/ui";
import { ContentBuilder } from "./ContentBuilder";

const EMPTY_DOC: BlockDocumentJson = { version: 1, blocks: [] };

/** Quote content editor — loads the quote, then hands its `content` to the
 *  shared ContentBuilder. Saves via PATCH quote/{id} (api 193). */
export function BlockBuilder() {
  const { quoteId = "" } = useParams();
  const id = Number(quoteId);
  const navigate = useNavigate();

  const [quote, setQuote] = useState<Quote | null>(null);
  const [lineItems, setLineItems] = useState<LineItem[]>([]);
  const [content, setContent] = useState<BlockDocumentJson>(EMPTY_DOC);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    staffApi
      .getQuote(id)
      .then((q) => {
        if (cancelled) return;
        setQuote(q);
        setLineItems(q.line_items ?? []);
        setContent(q.content && Array.isArray(q.content.blocks) ? q.content : EMPTY_DOC);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : "Could not load this quote.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const quoteContext = useMemo<QuoteContext | undefined>(() => {
    if (!quote) return undefined;
    return {
      quote_number: quote.quote_number,
      contact_first_name: quote.contact_first_name,
      contact_last_name: quote.contact_last_name,
      company_name: quote.company_name,
      site_first_line_of_address: quote.site_first_line_of_address,
      site_address_line_1: quote.site_address_line_1,
      site_town: quote.site_town,
      site_county: quote.site_county,
      site_postcode: quote.site_postcode,
      project_description: quote.project_description,
      expiry_date: quote.expiry_date,
      currency: quote.currency,
      vat_treatment: quote.vat_treatment,
    };
  }, [quote]);

  if (loading) return <FullPageSpinner label="Loading editor…" />;
  if (error || !quote) {
    return (
      <div className="builder__error-page">
        <p className="builder__error" role="alert">{error ?? "Quote not found."}</p>
        <Link to={`/staff/quotes/${id}`}><Button variant="secondary">Back to quote</Button></Link>
      </div>
    );
  }

  return (
    <ContentBuilder
      key={id}
      heading={<>Edit content <span className="numeric">· {quote.quote_number}</span></>}
      initialContent={content}
      lineItems={lineItems}
      quoteContext={quoteContext}
      warningSubject="quote"
      onSave={(doc) => staffApi.updateQuote(id, { content: doc }).then(() => undefined)}
      onBack={() => navigate(`/staff/quotes/${id}`)}
    />
  );
}
